import { useEffect } from "react";
import { BOOKING_URL, SERVICES } from "@/lib/site";
import { FadeUp, MaskedLine, GoldDot } from "@/components/Reveal";
import Booking from "@/components/Booking";
import Footer from "@/components/Footer";

const ServiceRow = ({ s, i, testid }) => (
  <FadeUp delay={Math.min(i * 0.06, 0.3)}>
    <a
      href={s.bookUrl || BOOKING_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="group grid items-center gap-3 border-b border-walnut/30 py-9 transition-colors duration-700 hover:bg-sand/30 lg:grid-cols-12 lg:gap-8 lg:px-6"
      data-testid={testid}
    >
      <span className="flex items-center gap-4 lg:col-span-1">
        <span className="h-[5px] w-[5px] rounded-full bg-gold opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />
        <span className="font-serif text-lg font-light italic text-walnut/65">
          {String(i + 1).padStart(2, "0")}
        </span>
      </span>
      <h3 className="font-serif text-2xl font-light tracking-wide text-forest transition-colors duration-500 group-hover:text-walnut lg:col-span-4 lg:text-3xl">
        {s.title}
        {s.tag && (
          <span className="mt-2 block font-sans text-[10px] font-medium uppercase tracking-[0.35em] text-gold" data-testid={`${testid}-tag`}>
            {s.tag}
          </span>
        )}
      </h3>
      <p className="font-sans text-sm font-light leading-relaxed tracking-wide text-walnut/85 lg:col-span-5">
        {s.desc}
      </p>
      <span className="flex flex-col gap-1 font-sans text-[10px] font-medium uppercase tracking-[0.25em] text-walnut/75 lg:col-span-2 lg:items-start">
        {s.prices.map(([min, price]) => (
          <span key={min} className="flex items-baseline whitespace-nowrap tabular-nums" data-testid={`${testid}-price-${min}`}>
            <span className="inline-block w-8 text-right">{min}</span>
            <span className="ml-2">min</span>
            <span className="ml-2 text-walnut">— ${price}</span>
          </span>
        ))}
        <span className="mt-1 h-px w-8 rounded-full bg-gold/80 transition-all duration-700 group-hover:w-14 group-hover:bg-gold" aria-hidden="true" />
      </span>
    </a>
  </FadeUp>
);

export default function ServicesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Services — Royal Essence Massage & Esthetics";
    return () => {
      document.title = "Royal Essence Massage — Massage Beyond Muscles";
    };
  }, []);

  const massage = SERVICES.filter((s) => s.cat === "massage");
  const esthetics = SERVICES.filter((s) => s.cat === "esthetics");

  return (
    <div className="bg-ivory font-sans text-walnut" data-testid="services-page">
      <main>
        <section className="bg-forest pb-24 pt-40 text-ivory lg:pb-28 lg:pt-48">
          <div className="mx-auto max-w-[1500px] px-6 lg:px-14">
            <h1 className="font-serif text-5xl font-light leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl" data-testid="services-headline">
              <MaskedLine delay={0.15}>Services,</MaskedLine>
              <MaskedLine delay={0.3}>
                <span className="italic text-sand">shaped to you</span>
                <span className="mb-2 ml-3 inline-block h-2 w-2 rounded-full bg-gold align-baseline" />
              </MaskedLine>
            </h1>
            <FadeUp delay={0.45}>
              <p className="mt-10 max-w-xl font-sans text-base font-light leading-relaxed tracking-wide text-ivory/75" data-testid="services-subcopy">
                Explore therapeutic massage and restorative skin care with a
                focus on comfort, function, and well-being.
              </p>
            </FadeUp>
          </div>
        </section>

        <section data-chapter="S1" className="py-24 lg:py-32" data-testid="massage-services">
          <div className="mx-auto max-w-[1500px] px-6 lg:px-14">
            <FadeUp className="mb-14 flex items-center gap-4">
              <GoldDot />
              <span className="font-sans text-[11px] font-medium uppercase tracking-[0.4em] text-gold">
                01 — Massage Services
              </span>
            </FadeUp>
            <div className="border-t border-walnut/30">
              {massage.map((s, i) => (
                <ServiceRow key={s.title} s={s} i={i} testid={`service-massage-${i}`} />
              ))}
            </div>
            <FadeUp delay={0.15}>
              <p className="mt-8 font-sans text-xs font-light italic tracking-wide text-walnut/75" data-testid="gst-note-massage">
                Prices are subject to GST.
              </p>
            </FadeUp>
          </div>
        </section>

        <section data-chapter="S2" className="bg-sand/40 py-24 lg:py-32" data-testid="esthetic-services">
          <div className="mx-auto max-w-[1500px] px-6 lg:px-14">
            <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
              <FadeUp className="flex items-center gap-4">
                <GoldDot />
                <span className="font-sans text-[11px] font-medium uppercase tracking-[0.4em] text-walnut/80">
                  02 — Facials &amp; Body Treatments
                </span>
              </FadeUp>
              <FadeUp delay={0.1}>
                <p className="font-sans text-sm font-light tracking-wide text-walnut/80" data-testid="esthetics-note">
                  Every esthetic session is a full 90 minutes.
                </p>
              </FadeUp>
            </div>
            <div className="border-t border-walnut/30">
              {esthetics.map((s, i) => (
                <ServiceRow key={s.title} s={s} i={i} testid={`service-esthetic-${i}`} />
              ))}
            </div>
            <FadeUp delay={0.15}>
              <p className="mt-8 font-sans text-xs font-light italic tracking-wide text-walnut/75" data-testid="gst-note-esthetics">
                Prices are subject to GST.
              </p>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="mt-10 max-w-xl font-serif text-xl font-light italic leading-snug text-walnut/90">
                “Not sure which treatment to choose?
                Book your session, and we’ll decide together what works best
                for you.”
              </p>
            </FadeUp>
          </div>
        </section>

        <Booking />
      </main>
      <Footer />
    </div>
  );
}
